import { authService } from '@services/AuthService';
import { AddFavoritesProductMutationParams } from '@services/AuthService/mutations/AddFavoritesProductMutation';
import { DeleteArchivedProductMutationParams } from '@services/AuthService/mutations/DeleteArchivedProductMutation';
import { DeleteFavoritesProductMutationParams } from '@services/AuthService/mutations/DeleteFavoritesProductMutation';
import {
  BitrixUser,
  FavoriteProduct,
  HistoryItem,
  IndividualOffer,
} from '@services/AuthService/query/loadUserQuery';
import { useStateSubscriber } from '@settings/core/stateContexts/useContextSubscriber';
import getEnv from '@settings/getEnv';
import Cookies from 'cookies-ts';
import { BehaviorSubject, filter, firstValueFrom } from 'rxjs';

import { BITRIX_USER_HISTORY_REFRESH_DELAY_MS, INDIVIDUAL_OFFERS_REFRESH_DELAY_MS } from './consts';

/**
 * Контекст bitrix пользователя
 */
export interface BitrixUserContext {
  /**
   * Флаг загрузки пользователя.
   * По умолчанию - true, по окончанию инциализации
   * уставливается в false
   */
  isUserLoading: boolean;
  /**
   * Флаг наличия авторизаванного пользователя.
   */
  isAuthorized: boolean;
  /**
   * Данные загруженного или дефолтного пользователя
   */
  bitrixUser: BitrixUser | null;
  /**
   * Акутальный список избранных продуктов пользователя
   */
  favorites: FavoriteProduct[];
  /**
   * Акутальный список архивных продуктов пользователя
   */
  archives: FavoriteProduct[];
  /**
   * Акутальный список истории поиска услуг доставки и доп. услуг
   */
  history: HistoryItem[];
  /**
   * Индивидуальные предложения, полученные пользователем
   */
  individualOffers: IndividualOffer[];
  /**
   * Индивидуальные предложения, созданные пользователем
   */
  createdIndividualOffers: IndividualOffer[];
  /**
   * Идет отложенное обновление списков ИП после создания
   */
  isIndividualOffersRefreshing: boolean;
}

export const $bitrixUserContext = new BehaviorSubject<BitrixUserContext>({
  isUserLoading: true,
  isAuthorized: false,
  bitrixUser: null,
  favorites: [],
  archives: [],
  history: [],
  individualOffers: [],
  createdIndividualOffers: [],
  isIndividualOffersRefreshing: false,
});

export const addFavoritesProduct = async (params: AddFavoritesProductMutationParams) => {
  const addedProduct = await authService().AddFavoritesProduct(params);

  if (addedProduct) {
    const currentState = $bitrixUserContext.getValue();

    $bitrixUserContext.next({
      ...currentState,
      favorites: [...currentState.favorites, addedProduct],
    });
  }
};

export const deleteFavoritesProduct = async (params: DeleteFavoritesProductMutationParams) => {
  const response = await authService().DeleteFavoritesProduct(params);

  if (response) {
    const currentState = $bitrixUserContext.getValue();

    $bitrixUserContext.next({
      ...currentState,
      favorites: currentState.favorites.filter((fav) => fav.id !== params.id),
    });
  }
};

const deleteArchivedProduct = async (params: DeleteArchivedProductMutationParams) => {
  const response = await authService().DeleteArchivedProduct(params);

  if (response) {
    const currentState = $bitrixUserContext.getValue();

    $bitrixUserContext.next({
      ...currentState,
      archives: currentState.archives.filter((fav) => fav.id !== params.id),
    });
  }
};

/**
 * getBitrixUserPipe возвращает subscriber для возможности подписки
 * на пользователя bitrix.
 */
export const getBitrixUserPipe = () => {
  return $bitrixUserContext.pipe();
};

/**
 * Числовой id авторизованного пользователя bitrix.
 */
export const getAuthorizedBitrixUserId = async (): Promise<number | undefined> => {
  const { bitrixUser, isAuthorized } = await firstValueFrom(
    getBitrixUserPipe().pipe(filter(({ isUserLoading }) => !isUserLoading))
  );
  const bitrixUserId = Number(bitrixUser?.id);

  if (!isAuthorized || !bitrixUser?.id || !Number.isFinite(bitrixUserId)) {
    return undefined;
  }

  return bitrixUserId;
};

/**
 * Функция открытия окна логина на встраиваемом сайте
 */
const showLogin = () => {
  window.dispatchEvent(new CustomEvent('openAuth'));

  const authLink = document.getElementById('auth_link');
  if (!authLink) {
    return;
  }

  authLink.click();
};

/**
 * Инициализация контекста bitrix пользователя.
 * При наличии токена происходит запрос текущего пользователя, без токена пользователь - null.
 */
const init = async () => {
  const cookies = new Cookies();
  const env = getEnv();
  const jwt = cookies.get(env.REACT_APP_AUTH_COOKIE);

  let user: BitrixUser | null = null;
  let isAuthorized = false;

  try {
    if (jwt && jwt.length > 0) {
      user = await authService().LoadUser(jwt);
      isAuthorized = !!user;
    }
  } catch (error) {
    console.error(error?.message);
  } finally {
    $bitrixUserContext.next({
      ...$bitrixUserContext.getValue(),
      bitrixUser: user,
      favorites: user?.favourites ?? [],
      archives: user?.archive ?? [],
      history: user?.history ?? [],
      individualOffers: user?.individual_offers ?? [],
      createdIndividualOffers: user?.list_created_individual_offers ?? [],
      isUserLoading: false,
      isAuthorized,
    });
  }
};

let historyRefreshTimerId: ReturnType<typeof setTimeout> | undefined;

/**
 * Загрузка истории поиска пользователя в контекст
 */
const loadBitrixUserHistory = async () => {
  const { isAuthorized, bitrixUser } = $bitrixUserContext.getValue();

  if (!isAuthorized || !bitrixUser) {
    return;
  }

  const cookies = new Cookies();
  const jwt = cookies.get(getEnv().REACT_APP_AUTH_COOKIE);

  if (!jwt || jwt.length === 0) {
    return;
  }

  try {
    const loadedHistory = await authService().LoadUserHistory(jwt);
    const currentState = $bitrixUserContext.getValue();
    const history = loadedHistory ?? currentState.history;

    $bitrixUserContext.next({
      ...currentState,
      bitrixUser: currentState.bitrixUser
        ? { ...currentState.bitrixUser, history }
        : currentState.bitrixUser,
      history,
    });
  } catch (error) {
    console.error(error?.message);
  }
};

/**
 * Отложенное обновление истории поиска пользователя
 */
export const refreshBitrixUserHistory = () => {
  clearTimeout(historyRefreshTimerId);
  historyRefreshTimerId = setTimeout(loadBitrixUserHistory, BITRIX_USER_HISTORY_REFRESH_DELAY_MS);
};

/**
 * Перезагрузка списков индивидуальных предложений пользователя (после создания или удаления).
 */
export const reloadIndividualOffers = async () => {
  const { isAuthorized, bitrixUser } = $bitrixUserContext.getValue();

  if (!isAuthorized || !bitrixUser) {
    return;
  }

  const jwt = new Cookies().get(getEnv().REACT_APP_AUTH_COOKIE);

  if (!jwt || jwt.length === 0) {
    return;
  }

  try {
    const user = await authService().LoadUser(jwt);

    if (!user) {
      return;
    }

    const currentState = $bitrixUserContext.getValue();

    $bitrixUserContext.next({
      ...currentState,
      bitrixUser: currentState.bitrixUser
        ? {
            ...currentState.bitrixUser,
            individual_offers: user.individual_offers,
            list_created_individual_offers: user.list_created_individual_offers,
          }
        : currentState.bitrixUser,
      individualOffers: user.individual_offers ?? [],
      createdIndividualOffers: user.list_created_individual_offers ?? [],
    });
  } catch (error) {
    console.error(error?.message);
  }
};

let individualOffersRefreshTimerId: ReturnType<typeof setTimeout> | undefined;

/**
 * Отложенное обновление списков ИП после создания: бэкенд привязывает ИП к пользователю асинхронно.
 */
export const refreshIndividualOffers = () => {
  clearTimeout(individualOffersRefreshTimerId);
  $bitrixUserContext.next({ ...$bitrixUserContext.getValue(), isIndividualOffersRefreshing: true });

  individualOffersRefreshTimerId = setTimeout(async () => {
    await reloadIndividualOffers();
    $bitrixUserContext.next({
      ...$bitrixUserContext.getValue(),
      isIndividualOffersRefreshing: false,
    });
  }, INDIVIDUAL_OFFERS_REFRESH_DELAY_MS);
};

const actions = {
  init,
  refreshBitrixUserHistory,
  reloadIndividualOffers,
  refreshIndividualOffers,
  showLogin,
  addFavoritesProduct,
  deleteFavoritesProduct,
  deleteArchivedProduct,
};

export const useBitrix = () => {
  const useBitrixUser = () => useStateSubscriber<BitrixUserContext>($bitrixUserContext);

  return {
    useBitrixUser,
    actions,
  };
};
